import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaRobot, FaCode, FaChartLine, FaCheck, FaArrowRight, FaEnvelope, FaPhone, FaMapMarkerAlt, FaFacebook, FaTwitter, FaLinkedin } from 'react-icons/fa';
import heroImage from '../assets/images/hero_image.png';
import intelligentResponseImage from '../assets/images/Inteligent_response.png';
import easyIntegrationImage from '../assets/images/Easy_integration.png';
import analyticsInsightImage from '../assets/images/Analytics_Insight.png';
import pricingImage from '../assets/images/pricing.png';
import neochatLogo from '../assets/images/neochat-logo.png';
import { useAuth } from '../context/AuthContext';

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white container">
      {/* Navigation */}
      <nav className="fixed w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img src={neochatLogo} alt="Neochat Logo" className="h-35px w-auto" />
            </div>
            <div className="flex items-center space-x-6">
              <Link to="/about" className="text-gray-600 hover:text-gray-900">About Us</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900 ml-2">Contact</Link>
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Business Identity Section */}
      <section className="pt-32 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Neochat AI Solutions</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Neochat is a leading provider of AI-powered chatbot solutions, helping businesses enhance customer service, automate tasks, and scale operations efficiently. Our mission is to make AI accessible and valuable for businesses of all sizes.
            </p>
          </div>
        </div>
      </section>

      

      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 overflow-hidden container">
        {/* Decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Transform Your Business with AI-Powered Chatbots
              </h1>
              <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto lg:mx-0">
                Neochat provides intelligent, customizable AI assistants that integrate seamlessly with your business. Enhance customer service, automate tasks, and scale your operations efficiently.
              </p>
              <div className="flex justify-center lg:justify-start gap-4">
                <Link
                  to="/login"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors transform hover:scale-105"
                >
                  Login
                </Link>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative z-10 rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={heroImage} 
                  alt="AI Chatbot Interface" 
                  className="w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Key Features</h2>
            <p className="text-xl text-gray-600">Everything you need to create powerful AI assistants</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <FaRobot className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Intelligent Responses</h3>
              <p className="text-gray-600 mb-4">Advanced AI models provide accurate and context-aware responses to your users.</p>
              <div className="relative h-32 overflow-hidden rounded-xl">
                <img
                  src={intelligentResponseImage}
                  alt="Intelligent Responses"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <FaCode className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Easy Integration</h3>
              <p className="text-gray-600 mb-4">Simple SDK integration with your existing website or application.</p>
              <div className="relative h-32 overflow-hidden rounded-xl">
                <img
                  src={easyIntegrationImage}
                  alt="Easy Integration"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mb-4">
                <FaChartLine className="w-6 h-6 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Analytics & Insights</h3>
              <p className="text-gray-600 mb-4">Comprehensive dashboard to monitor performance and user interactions.</p>
              <div className="relative h-32 overflow-hidden rounded-xl">
                <img
                  src={analyticsInsightImage}
                  alt="Analytics & Insights"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Simple, Transparent Pricing</h2>
            <p className="text-xl text-gray-600">Start with one agent and scale as you grow</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center mr-4">
                    <FaRobot className="w-6 h-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Base Plan</h3>
                    <p className="text-gray-600">Perfect for getting started</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">1 AI Agent</span>
                  </div>
                  <div className="flex items-center">
                    <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">Unlimited Conversations</span>
                  </div>
                  <div className="flex items-center">
                    <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                    <span className="text-gray-700">Basic Analytics</span>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="flex items-baseline mb-6">
                    <span className="text-4xl font-bold text-gray-900">$10</span>
                    <span className="text-gray-600 ml-2">/month</span>
                  </div>
                  <div className="text-sm text-gray-600 mb-6">
                    + $0.01 per message after 1000 messages
                  </div>
                  {isAuthenticated ? (
                    <Link
                      to="/dashboard"
                      className="block w-full bg-indigo-600 text-white text-center py-3 px-6 rounded-xl hover:bg-indigo-700 transition-colors"
                    >
                      Go to Dashboard
                    </Link>
                  ) : (
                    <Link
                      to="/login"
                      className="block w-full bg-indigo-600 text-white text-center py-3 px-6 rounded-xl hover:bg-indigo-700 transition-colors"
                    >
                      Login
                    </Link>
                  )}
                </div>
              </div>
              
            </div>
            <div className="relative">
              <img
                src={pricingImage}
                alt="Pricing illustration"
                className="w-full h-auto rounded-2xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Product Information Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Solutions</h2>
            <p className="text-xl text-gray-600">Comprehensive AI chatbot solutions for your business needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 p-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">AI Customer Service Bot</h3>
              <p className="text-gray-600 mb-6">24/7 automated customer support with natural language understanding and context-aware responses.</p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Multi-language support</span>
                </div>
                <div className="flex items-center">
                  <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Integration with major platforms</span>
                </div>
                <div className="flex items-center">
                  <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Customizable responses</span>
                </div>
              </div>
              <div className="mt-6">
                <Link
                  to="/contact"
                  className="text-indigo-600 hover:text-indigo-700 font-semibold"
                >
                  Contact us for pricing →
                </Link>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 p-4 mt-4">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Enterprise AI Solutions</h3>
              <p className="text-gray-600 mb-6">Custom AI solutions tailored to your specific business requirements and workflows.</p>
              <div className="space-y-4">
                <div className="flex items-center">
                  <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Custom AI model training</span>
                </div>
                <div className="flex items-center">
                  <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">API integration</span>
                </div>
                <div className="flex items-center">
                  <FaCheck className="w-5 h-5 text-green-500 mr-3" />
                  <span className="text-gray-700">Dedicated support</span>
                </div>
              </div>
              <div className="mt-6">
                <Link
                  to="/contact"
                  className="text-indigo-600 hover:text-indigo-700 font-semibold"
                >
                  Request a demo →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Footer with Legal Links */}
      <footer className="bg-gray-900 text-white py-12 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Neochat</h3>
              <p className="text-gray-400">AI-powered chatbot solutions for modern businesses.</p>
              
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white">About Us</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white">Terms & Conditions</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2">
                <li className="text-gray-400">support@neochat.com</li>
                <li className="text-gray-400">+6281-1821-8365</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>© {new Date().getFullYear()} Neochat. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing; 